import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Machine } from '@couch/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { openComfy } from './open-comfy';

export const MACHINES_KEY = ['machines'] as const;

function RenameForm({ machine, onDone }: { machine: Machine; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(machine.name);
  const rename = useMutation({
    mutationFn: () => api.renameMachine(machine.id, name),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: MACHINES_KEY }); onDone(); },
  });
  return (
    <form className="rename-form" onSubmit={(e) => { e.preventDefault(); rename.mutate(); }}>
      <label htmlFor={`rename-${machine.id}`}>Machine name</label>
      <input id={`rename-${machine.id}`} value={name} maxLength={64} onChange={(e) => { setName(e.target.value); }} />
      <button type="submit" disabled={rename.isPending}>Save</button>
      <button type="button" className="button-quiet" onClick={onDone}>Cancel</button>
      {rename.isError && <p role="alert" className="form-error">{errorMessage(rename.error)}</p>}
    </form>
  );
}

export function MachineActions({ machine }: { machine: Machine }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  const [renaming, setRenaming] = useState(false);
  const restart = useMutation({
    mutationFn: () => api.restartMachine(machine.id),
    onSuccess: () => { setMessage({ text: 'ComfyUI is restarting.', error: false }); },
    onError: (e) => { setMessage({ text: errorMessage(e), error: true }); },
  });
  const wake = useMutation({
    mutationFn: () => api.wakeMachine(machine.id),
    onSuccess: ({ helper }) => {
      const text = machine.cloudPodId ? `Starting ${machine.name} on RunPod — it shows up as Online in about 1–3 minutes.` : `Wake signal sent through ${helper} — ${machine.name} usually shows up within a minute.`;
      setMessage({ text, error: false });
    },
    onError: (e) => { setMessage({ text: errorMessage(e), error: true }); },
  });
  const remove = useMutation({
    mutationFn: () => api.removeMachine(machine.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MACHINES_KEY }),
    onError: (e) => { setMessage({ text: errorMessage(e), error: true }); },
  });
  const confirmRemove = () => {
    const pod = machine.cloudPodId ? ' The pod keeps running at RunPod and costs money — to end it, use Delete pod on the machine page.' : '';
    if (window.confirm(`Remove ${machine.name}? The agent on that machine stops; ComfyUI and models stay on the machine.${pod}`)) remove.mutate();
  };
  if (renaming) return <RenameForm machine={machine} onDone={() => { setRenaming(false); }} />;
  return (
    <div className="machine-actions">
      {machine.online && (
        <>
          <button type="button" aria-label={`Open ComfyUI on ${machine.name}`} onClick={() => { openComfy(machine.id, (text) => { setMessage({ text, error: true }); }); }}>Open ComfyUI</button>
          <button type="button" className="button-quiet" aria-label={`Restart ComfyUI on ${machine.name}`} disabled={restart.isPending} onClick={() => { restart.mutate(); }}>Restart ComfyUI</button>
        </>
      )}
      {!machine.online && machine.canWake && (
        <button type="button" aria-label={machine.cloudPodId ? `Start ${machine.name} on RunPod` : `Wake up ${machine.name}`} disabled={wake.isPending} onClick={() => { wake.mutate(); }}>
          {machine.cloudPodId ? 'Start' : 'Wake up'}
        </button>
      )}
      <Link className="button-link" to={`/machines/${machine.id}`} aria-label={`Manage ${machine.name}`}>Manage</Link>
      <button type="button" className="button-quiet" aria-label={`Rename ${machine.name}`} onClick={() => { setRenaming(true); }}>Rename</button>
      <button type="button" className="button-quiet" aria-label={`Remove ${machine.name}`} disabled={remove.isPending} onClick={confirmRemove}>Remove</button>
      {message && <p role={message.error ? 'alert' : 'status'} className={message.error ? 'form-error' : 'success'}>{message.text}</p>}
    </div>
  );
}
